import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { Catalogo } from '../../../../../interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { CatalogoService } from '../../../../../services/shared/catalogo.service';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';
import { CatalogoDinamico } from '../../../../../interfaces/interface-bakend/shared/catalogo/catalogo-dinamico.interface';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-rubros-alta',
  templateUrl: './rubro-alta.component.html',
  styleUrls: ['./rubro-alta.component.scss']
})
export class RubroAltaComponent implements OnInit {

  form: FormGroup;
  catalogo: Catalogo;
  especialidades: CatalogoDinamico[] = []; // lista de todas las especialidades
  especialidadesOrdenadas: CatalogoDinamico[] = []; // especialidades ordenadas para mostrar
  especialidadesSeleccionadas: Set<string> = new Set(); // IDs de especialidades seleccionadas
  rubros: Catalogo[] = []; // todos los rubros para buscar nombres
  rubroMap: Map<string, string> = new Map(); // mapa _id -> nombre de rubro

  constructor(
    private router: Router,
    private catalogoService: CatalogoService,
    public guiMsjService: GuiMsjService,
  ) {
    this.createForm();
    this.getCatalogo();
  }

  ngOnInit(): void {
    this.obtenerDatosIniciales();
  }

  get rubroNoValido() {
    return this.form.get('rubro').invalid && this.form.get('rubro').touched;
  }

  async getCatalogo() {
    this.catalogo = await this.catalogoService.getFindByKey(EntidadesKeys.RUBROS);
  }

  // Obtener todos los rubros para crear el mapa
  async getRubros() {
    try {
      this.rubros = await this.catalogoService.findDescendantsByKey(EntidadesKeys.RUBROS);
      this.rubroMap.clear();
      this.rubros.forEach(rubro => {
        this.rubroMap.set(rubro._id!, rubro.concepto!);
      });
    } catch (error) {
      console.error('Error al obtener rubros:', error);
    }
  }

  // Método para obtener el nombre del rubro de una especialidad
  getNombreRubro(especialidad: CatalogoDinamico): string {
    if (!especialidad.properties || !especialidad.properties.rubro) {
      return '';
    }
    
    const rubro = especialidad.properties.rubro;
    
    // Si es string (ID)
    if (typeof rubro === 'string') {
      return this.rubroMap.get(rubro) || '';
    }
    
    // Si es un objeto, hacer cast a any y verificar _id
    if (typeof rubro === 'object' && rubro !== null) {
      const rubroObj = rubro as any; // Cast a any para acceder a propiedades dinámicas
      if (rubroObj._id) {
        return this.rubroMap.get(rubroObj._id) || '';
      }
    }
    
    return '';
  }

  // Método para obtener todas las especialidades
  async getEspecialidades() {
    try {
      this.especialidades = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES) as CatalogoDinamico[];
      this.ordenarEspecialidades();
    } catch (error) {
      console.error('Error al obtener especialidades:', error);
    }
  }

  // Ordenar especialidades: primero por rubro, luego alfabéticamente
  ordenarEspecialidades() {
    this.especialidadesOrdenadas = [...this.especialidades].sort((a, b) => {
      const rubroA = this.getNombreRubro(a);
      const rubroB = this.getNombreRubro(b);
      
      if ((rubroA && rubroB) || (!rubroA && !rubroB)) {
        return a.concepto!.localeCompare(b.concepto!);
      }
      return rubroA ? -1 : 1;
    });
  }

  // Obtener todos los datos iniciales
  async obtenerDatosIniciales() {
    await Promise.all([
      this.getRubros(),
      this.getEspecialidades()
    ]);
  }

  // Manejar selección de especialidades
  onEspecialidadSeleccionada(event: any, especialidadId: string) {
    if (event.checked) {
      this.especialidadesSeleccionadas.add(especialidadId);
    } else {
      this.especialidadesSeleccionadas.delete(especialidadId);
    }
  }

  createForm() {
    this.form = new FormGroup({
      rubro: new FormControl('', [Validators.required, Validators.min(3)]),
      activo: new FormControl(false, [Validators.required]),
    });
  }

  async setDataForma(): Promise<Catalogo> {
    const data: Catalogo = {
      key: null,
      concepto: this.form.value.rubro,
      logico_1: this.form.value.activo,
      parent: this.catalogo._id!,
      properties: {},
    };
    return data;
  }

  // Método para asignar el rubro a las especialidades seleccionadas
  async asignarRubroAEspecialidades(rubroId: string, especialidadesIds: string[]) {
    if (especialidadesIds.length === 0) return;
    
    const promesas = especialidadesIds.map(async especialidadId => {
      const especialidad = this.especialidades.find(e => e._id === especialidadId);
      if (!especialidad) return;
      
      // Crear una copia de properties existentes o un objeto vacío
      const existingProperties = especialidad.properties || {};
      
      const datosActualizados: CatalogoDinamico = {
        ...especialidad,
        properties: {
          ...existingProperties,
          rubro: rubroId  // Asignamos solo el ID string
        }
      };
      
      return this.catalogoService.put(especialidadId, datosActualizados)
        .catch(error => {
          console.error(`Error al actualizar especialidad ${especialidadId}:`, error);
          return null;
        });
    });
    
    await Promise.all(promesas);
  }

  async createRubro() {
    await this.guiMsjService.msjFormSubmit('Guardando');
    
    try {
      // 1. Crear el rubro
      const rubroCreado: Catalogo = await this.catalogoService.post(await this.setDataForma());
      
      console.log(`Rubro creado: ${rubroCreado._id}, asignando ${this.especialidadesSeleccionadas.size} especialidades`);
      
      // 2. Asignar el nuevo rubro a las especialidades seleccionadas
      if (this.especialidadesSeleccionadas.size > 0) {
        const idsSeleccionadas = Array.from(this.especialidadesSeleccionadas);
        await this.asignarRubroAEspecialidades(rubroCreado._id!, idsSeleccionadas);
      }
      
      Swal.fire({
        icon: 'success',
        title: 'Rubro guardado satisfactoriamente',
        showConfirmButton: false,
        timer: 1100,
      });
      this.router.navigateByUrl('/rubros-listado');
      
    } catch (error) {
      console.error('Error al crear rubro:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al crear rubro',
      });
    }
  }
}